import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
  Svg,
  Rect,
  pdf,
} from "@react-pdf/renderer";
import {
  calculateGroupDiscountTotal,
  calculateGroupGrossTotal,
  calculateGroupTotal,
} from "@/lib/finance-calculations";
import { formatShortDateRange, formatDate } from "@/lib/date-utils";
import { getInvoiceLabels } from "@/lib/languages";
import {
  getInvoicePaymentReference,
  formatQRReference,
  generateSwissQRCode,
  getCompanyInfo,
  type SwissQRRect,
} from "@/lib/swiss-qr-bill";
import { uploadPDFToS3 } from "@/lib/quote-pdf-generator";
import logoImage from "@/assets/logo-livemotion.png";

// Re-export uploadPDFToS3 for convenience
export { uploadPDFToS3 };

// ─── Styles ────────────────────────────────────────────────────────
// Replicates quote-pdf-generator styles with invoice-specific additions
const styles = StyleSheet.create({
  page: {
    fontFamily: "Open Sans",
    fontSize: 10,
    paddingBottom: 60,
    paddingHorizontal: 50,
    paddingTop: 40,
  },
  headerSection: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 15,
  },
  leftContainer: {},
  logo: {
    width: 155,
    height: 35,
  },
  headerRight: {
    width: "250",
  },
  headerTitle: {
    fontSize: 15,
    fontWeight: 700,
    marginBottom: 8,
  },
  metaSection: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 25,
  },
  metaLeft: {
    fontSize: 9,
    color: "#000000",
  },
  metaRight: {
    alignItems: "flex-start",
  },
  metaRow: {
    flexDirection: "row",
    marginBottom: 2,
  },
  metaLabel: {
    fontSize: 9,
    width: 115,
  },
  metaValue: {
    fontSize: 9,
  },
  customerSection: {
    marginBottom: 25,
  },
  customerLabel: {
    fontSize: 9,
    marginBottom: 8,
    fontStyle: "italic",
  },
  customerName: {
    fontSize: 9,
    marginBottom: 0,
  },
  customerAddress: {
    fontSize: 9,
    marginBottom: 2,
  },
  titleSection: {
    marginBottom: 15,
  },
  mainTitle: {
    fontSize: 14,
    fontWeight: 700,
    marginBottom: 10,
  },
  projectInfoRow: {
    flexDirection: "row",
    marginBottom: 20,
  },
  projectInfoItem: {
    flexDirection: "row",
    marginRight: 40,
  },
  projectInfoLabel: {
    fontSize: 9,
    flexBasis: "100",
    marginRight: 4,
  },
  projectInfoValue: {
    fontSize: 9,
    textAlign: "left",
    flexBasis: "100",
  },
  summarySection: {
    marginTop: 20,
  },
  summaryTitle: {
    fontSize: 12,
    fontWeight: 700,
  },
  summaryTable: {
    width: "100%",
  },
  summaryHeader: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#000000",
    paddingBottom: 3,
    marginBottom: 4,
    alignItems: "flex-end",
  },
  summaryRow: {
    flexDirection: "row",
    paddingVertical: 1,
  },
  summaryColName: { width: "49%" },
  summaryColTotal: {
    width: "17%",
    textAlign: "right",
  },
  summaryColDiscount: {
    width: "17%",
    textAlign: "right",
  },
  summaryColNet: {
    width: "17%",
    textAlign: "right",
  },
  detailTitle: {
    fontSize: 12,
    fontWeight: 700,
    marginBottom: 15,
  },
  groupSection: {
    marginBottom: 20,
  },
  groupTitle: {
    fontSize: 11,
    fontWeight: 700,
    marginBottom: 10,
  },
  table: {
    width: "100%",
  },
  tableHeader: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#000000",
    paddingBottom: 3,
    marginBottom: 4,
  },
  tableRow: {
    flexDirection: "row",
    paddingVertical: 1,
  },
  subGroupHeader: {
    flexDirection: "row",
    paddingTop: 4,
    paddingBottom: 1,
  },
  colAmount: { width: "5%", textAlign: "left" },
  colFactor: { width: "5%", textAlign: "center" },
  colName: { width: "69%", paddingLeft: 10 },
  colNameNarrow: { width: "64%", paddingLeft: 10 },
  colDiscount: { width: "7%", textAlign: "right" },
  colUnitPrice: { width: "10%", textAlign: "right" },
  colTotal: { width: "10%", textAlign: "right" },
  headerText: {
    fontSize: 10,
    fontWeight: 600,
  },
  cellText: {
    fontSize: 10,
  },
  externalNoteRow: {
    paddingTop: 2,
    paddingBottom: 2,
    marginLeft: "14%",
  },
  externalNoteText: {
    fontSize: 10,
    fontWeight: 300,
    fontStyle: "italic",
    color: "#666666",
    paddingLeft: 10,
  },
  subGroupName: {
    fontSize: 10,
    fontWeight: 700,
  },
  groupSubtotal: {
    flexDirection: "row",
    justifyContent: "space-between",
    borderTopWidth: 1,
    borderTopColor: "#000000",
    marginTop: 4,
    paddingTop: 3,
  },
  groupSubtotalLabel: {
    fontSize: 10,
    fontWeight: 700,
  },
  groupSubtotalValue: {
    fontSize: 10,
    fontWeight: 700,
  },
  totalsSection: {
    marginTop: 8,
  },
  totalRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingVertical: 1,
  },
  totalRowWithBorder: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingVertical: 1,
    borderTopWidth: 1,
    borderTopColor: "#cccccc",
  },
  totalRowFinal: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingVertical: 3,
    borderTopWidth: 2,
    borderTopColor: "#000000",
    marginTop: 3,
  },
  totalLabel: {
    fontSize: 10,
  },
  totalLabelBold: {
    fontSize: 10,
    fontWeight: 700,
  },
  totalValue: {
    fontSize: 10,
    textAlign: "right",
  },
  totalValueBold: {
    fontSize: 10,
    fontWeight: 700,
    textAlign: "right",
  },
  totalValueFinal: {
    fontSize: 11,
    fontWeight: 700,
    textAlign: "right",
  },
  footer: {
    position: "absolute",
    bottom: 20,
    left: 50,
    right: 50,
    borderTopWidth: 0.5,
    borderTopColor: "#000000",
    paddingTop: 6,
  },
  footerInner: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
  footerLeft: {
    textAlign: "center",
    flex: 1,
  },
  footerText: {
    fontSize: 8,
    fontWeight: 300,
    color: "#000000",
    marginBottom: 2,
  },
  footerPageNumber: {
    fontSize: 8,
    fontWeight: 300,
    color: "#000000",
  },
  optionalBadge: {
    fontSize: 11,
    color: "#666666",
    fontWeight: 300,
  },
  // QR-bill specific styles
  qrBillPage: {
    fontFamily: "Open Sans",
    fontSize: 8,
    padding: 0,
  },
  qrBillContainer: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    height: "105mm",
    borderTopWidth: 1,
    borderTopColor: "#000000",
    borderTopStyle: "dashed",
    flexDirection: "row",
  },
  qrReceiptSection: {
    width: "62mm",
    paddingHorizontal: "5mm",
    paddingTop: "5mm",
    borderRightWidth: 1,
    borderRightColor: "#000000",
    borderRightStyle: "dashed",
  },
  qrPaymentSection: {
    flex: 1,
    paddingHorizontal: "5mm",
    paddingTop: "5mm",
  },
  qrSectionTitle: {
    fontSize: 11,
    fontWeight: 700,
    marginBottom: 4,
  },
  qrHeading: {
    fontSize: 6,
    fontWeight: 700,
    marginTop: 6,
    marginBottom: 2,
  },
  qrText: {
    fontSize: 8,
    marginBottom: 1,
  },
  qrTextSmall: {
    fontSize: 7,
    marginBottom: 1,
  },
  qrAmountSection: {
    flexDirection: "row",
    marginTop: 6,
  },
  qrAmountBlock: {
    marginRight: 10,
  },
  qrImage: {
    width: "46mm",
    height: "46mm",
    aspectRatio: 1,
    // SIX recommends 5 mm of unprinted quiet space around the 46 mm code.
    marginTop: "5mm",
    marginBottom: "5mm",
  },
  qrPaymentRow: {
    flexDirection: "row",
    marginTop: 4,
  },
  qrPaymentLeft: {
    width: "56mm",
    marginRight: "5mm",
  },
  qrPaymentRight: {
    flex: 1,
  },
  reminderTitle: {
    fontSize: 16,
    fontWeight: 700,
    marginBottom: 22,
  },
  reminderText: {
    fontSize: 10,
    lineHeight: 1.6,
    marginBottom: 24,
  },
  reminderAmountBox: {
    borderTopWidth: 1,
    borderTopColor: "#000000",
    borderBottomWidth: 1,
    borderBottomColor: "#000000",
    paddingVertical: 10,
    marginTop: 8,
  },
  reminderAmountRow: {
    flexDirection: "row",
    justifyContent: "space-between",
  },
  reminderAmountLabel: {
    fontSize: 11,
    fontWeight: 700,
  },
  reminderAmountValue: {
    fontSize: 11,
    fontWeight: 700,
  },
});

// ─── Types ─────────────────────────────────────────────────────────

interface InvoiceLineItem {
  id: string;
  name: string;
  amount: number;
  factor: number;
  unitPrice: number;
  discount: number;
  total: number;
  isTitle: boolean;
  externalNote?: string;
}

interface InvoiceGroup {
  id: string;
  name: string;
  optional: boolean;
  applyProjectDiscount?: boolean;
  items: InvoiceLineItem[];
}

interface InvoiceContent {
  groups: InvoiceGroup[];
}

export interface InvoicePDFData {
  invoiceTitle: string;
  invoiceNumber: string;
  invoiceDate: string;
  dueDate: string;
  paymentTermDays: number;
  depositPercent?: number;
  projectDiscountPercent: number;
  vatRate: "CH_8_1" | "NONE";
  currency: "CHF" | "EUR";
  content: InvoiceContent;
  deductions?: Array<{ description: string; amount: number }>;
  includeQrBill: boolean;
  qrCodeRects?: SwissQRRect[];
  qrReference?: string;
  language?: string;
  customerName?: string;
  customerAddress?: string;
  contactName?: string;
  accountManagerName?: string;
  eventStartDate?: string;
  eventEndDate?: string;
  customerReference?: string;
  paymentReminder?: {
    text: string;
    originalInvoice: InvoicePDFData;
  };
}

const COMPANY_FOOTER =
  "livemotion gmbh, Bolacker 1, 4563 Gerlafingen, CHE-238.506.059 MWST";

const BANK_FOOTERS = {
  CHF: "IBAN CH31 8080 8008 6983 2167 4, SWIFT-BIC: RAIFCH22, Raiffeisenbank, 8917 Oberlunkhofen",
  EUR: "IBAN LT843250039777957056, SWIFT-BIC: REVOLT21, Revolut Bank UAB 08130, Vilnius, Lithuania",
} as const;

export function getInvoiceBankDetails(currency: "CHF" | "EUR") {
  return {
    company: COMPANY_FOOTER,
    bank: BANK_FOOTERS[currency],
  };
}

function getCurrencyLabels(language: string, currency: "CHF" | "EUR") {
  const labels = getInvoiceLabels(language);
  if (currency === "CHF") return labels;
  return {
    ...labels,
    totalCol: labels.totalCol.replace("CHF", "EUR"),
    netPrice: labels.netPrice.replace("CHF", "EUR"),
    currency: "EUR",
    currencyPrefix: "EUR",
  };
}

// ─── Helpers ───────────────────────────────────────────────────────

const formatCurrency = (amount: number): string => {
  return amount.toLocaleString("de-CH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

function calculateTotals(data: InvoicePDFData): {
  netTotal: number;
  optionalTotal: number;
  discountAmount: number;
  discountedTotal: number;
  deductionsTotal: number;
  totalAfterDeductions: number;
  vatRateValue: number;
  vatAmount: number;
  grossTotal: number;
  depositPercent: number;
  invoicedTotal: number;
} {
  let netTotal = 0;
  let optionalTotal = 0;
  let discountEligibleTotal = 0;

  for (const group of data.content.groups) {
    const groupTotal = calculateGroupTotal(group.items);
    if (group.optional) {
      optionalTotal += groupTotal;
    } else {
      netTotal += groupTotal;
      if (group.applyProjectDiscount ?? true) {
        discountEligibleTotal += groupTotal;
      }
    }
  }

  const discountAmount =
    data.projectDiscountPercent > 0
      ? discountEligibleTotal * (data.projectDiscountPercent / 100)
      : 0;
  const discountedTotal = netTotal - discountAmount;

  const deductionsTotal =
    data.deductions?.reduce((sum, d) => sum + d.amount, 0) ?? 0;
  const totalAfterDeductions = discountedTotal - deductionsTotal;

  const vatRateValue = data.vatRate === "CH_8_1" ? 8.1 : 0;
  const vatAmount = totalAfterDeductions * (vatRateValue / 100);
  const grossTotal = totalAfterDeductions + vatAmount;

  const effectiveDepositPercent =
    data.depositPercent && data.depositPercent > 0 && data.depositPercent <= 100
      ? data.depositPercent
      : 100;
  const invoicedTotal =
    effectiveDepositPercent < 100
      ? Math.round(grossTotal * (effectiveDepositPercent / 100) * 100) / 100
      : grossTotal;

  return {
    netTotal,
    optionalTotal,
    discountAmount,
    discountedTotal,
    deductionsTotal,
    totalAfterDeductions,
    vatRateValue,
    vatAmount,
    grossTotal,
    depositPercent: effectiveDepositPercent,
    invoicedTotal,
  };
}

function getPaymentReminderLabels(language: string) {
  if (language === "fr") {
    return {
      title: "Rappel",
      reminderDate: "Date du rappel",
      amountDue: "Montant ouvert",
      days: "jours",
    };
  }
  if (language === "en") {
    return {
      title: "Payment Reminder",
      reminderDate: "Reminder date",
      amountDue: "Outstanding amount",
      days: "days",
    };
  }
  return {
    title: "Mahnung",
    reminderDate: "Mahndatum",
    amountDue: "Offener Betrag",
    days: "Tage",
  };
}

// ─── Group Table Component ─────────────────────────────────────────

const GroupTable = ({
  group,
  isOptional = false,
  labels,
}: {
  group: InvoiceGroup;
  isOptional?: boolean;
  labels: ReturnType<typeof getInvoiceLabels>;
}) => {
  const groupTotal = calculateGroupTotal(group.items);
  const regularItems = group.items.filter((item) => !item.isTitle);
  const hasDiscount = regularItems.some((item) => item.discount > 0);
  const nameCol = hasDiscount ? styles.colNameNarrow : styles.colName;

  return (
    <View style={styles.groupSection} wrap={false}>
      <View style={{ flexDirection: "row", alignItems: "baseline", gap: 8 }}>
        <Text style={styles.groupTitle}>{group.name}</Text>
        {isOptional && (
          <Text style={styles.optionalBadge}>{labels.optional}</Text>
        )}
      </View>
      <View style={styles.table}>
        <View style={styles.tableHeader}>
          <Text style={[styles.headerText, styles.colAmount]}>
            {labels.amount}
          </Text>
          <Text style={[styles.headerText, styles.colFactor]}>
            {labels.factor}
          </Text>
          <Text style={[styles.headerText, nameCol]}>{labels.name}</Text>
          <Text style={[styles.headerText, styles.colUnitPrice]}>
            {labels.unitPrice}
          </Text>
          {hasDiscount && (
            <Text style={[styles.headerText, styles.colDiscount]}>
              {labels.discount}
            </Text>
          )}
          <Text style={[styles.headerText, styles.colTotal]}>
            {labels.totalCol}
          </Text>
        </View>
        {group.items.map((item) => (
          <View key={item.id}>
            {item.isTitle ? (
              <View style={styles.subGroupHeader}>
                <View style={{ width: "5%" }} />
                <View style={{ width: "5%" }} />
                <Text style={[styles.subGroupName, nameCol]}>{item.name}</Text>
              </View>
            ) : (
              <>
                <View style={styles.tableRow}>
                  <Text style={[styles.cellText, styles.colAmount]}>
                    {item.amount}
                  </Text>
                  <Text style={[styles.cellText, styles.colFactor]}>
                    {item.factor}
                  </Text>
                  <Text style={[styles.cellText, nameCol]}>{item.name}</Text>
                  <Text style={[styles.cellText, styles.colUnitPrice]}>
                    {formatCurrency(item.unitPrice)}
                  </Text>
                  {hasDiscount && (
                    <Text style={[styles.cellText, styles.colDiscount]}>
                      {item.discount > 0 ? `${item.discount}%` : ""}
                    </Text>
                  )}
                  <Text style={[styles.cellText, styles.colTotal]}>
                    {formatCurrency(item.total)}
                  </Text>
                </View>
                {item.externalNote && (
                  <View style={styles.externalNoteRow}>
                    <Text style={styles.externalNoteText}>
                      {item.externalNote}
                    </Text>
                  </View>
                )}
              </>
            )}
          </View>
        ))}
      </View>
      <View style={styles.groupSubtotal}>
        <Text style={styles.groupSubtotalLabel}>
          {labels.total} {group.name}:
        </Text>
        <Text style={styles.groupSubtotalValue}>
          {formatCurrency(groupTotal)}
        </Text>
      </View>
    </View>
  );
};

// ─── QR-Bill Section ───────────────────────────────────────────────

const QRBillSection = ({
  data,
  totals,
  labels,
}: {
  data: InvoicePDFData;
  totals: ReturnType<typeof calculateTotals>;
  labels: ReturnType<typeof getInvoiceLabels>;
}) => {
  const company = getCompanyInfo();

  return (
    <View style={styles.qrBillContainer}>
      {/* Receipt Section (left, 62mm) */}
      <View style={styles.qrReceiptSection}>
        <Text style={styles.qrSectionTitle}>Empfangsschein</Text>

        <Text style={styles.qrHeading}>{labels.qrBillPayableTo}</Text>
        <Text style={styles.qrTextSmall}>CH31 8080 8008 6983 2167 4</Text>
        <Text style={styles.qrTextSmall}>{company.name}</Text>
        <Text style={styles.qrTextSmall}>{company.street}</Text>
        <Text style={styles.qrTextSmall}>
          {company.postalCode} {company.city}
        </Text>

        {data.qrReference && (
          <>
            <Text style={styles.qrHeading}>{labels.qrBillReference}</Text>
            <Text style={styles.qrTextSmall}>
              {formatQRReference(data.qrReference)}
            </Text>
          </>
        )}

        {data.customerName && (
          <>
            <Text style={styles.qrHeading}>{labels.qrBillPayableBy}</Text>
            <Text style={styles.qrTextSmall}>{data.customerName}</Text>
            {data.customerAddress && (
              <Text style={styles.qrTextSmall}>{data.customerAddress}</Text>
            )}
          </>
        )}

        <View style={styles.qrAmountSection}>
          <View style={styles.qrAmountBlock}>
            <Text style={styles.qrHeading}>{labels.qrBillCurrency}</Text>
            <Text style={styles.qrTextSmall}>CHF</Text>
          </View>
          <View style={styles.qrAmountBlock}>
            <Text style={styles.qrHeading}>{labels.qrBillAmount}</Text>
            <Text style={styles.qrTextSmall}>
              {formatCurrency(totals.invoicedTotal)}
            </Text>
          </View>
        </View>
      </View>

      {/* Payment Section (right) */}
      <View style={styles.qrPaymentSection}>
        <Text style={styles.qrSectionTitle}>Zahlteil</Text>

        <View style={styles.qrPaymentRow}>
          {/* Left: QR code + amount */}
          <View style={styles.qrPaymentLeft}>
            {data.qrCodeRects && (
              <Svg style={styles.qrImage} viewBox="0 0 46 46">
                {data.qrCodeRects.map((rect, index) => (
                  <Rect
                    key={`${index}-${rect.fill}`}
                    x={rect.x}
                    y={rect.y}
                    width={rect.width}
                    height={rect.height}
                    fill={rect.fill}
                  />
                ))}
              </Svg>
            )}

            <View style={styles.qrAmountSection}>
              <View style={styles.qrAmountBlock}>
                <Text style={styles.qrHeading}>{labels.qrBillCurrency}</Text>
                <Text style={styles.qrText}>CHF</Text>
              </View>
              <View style={styles.qrAmountBlock}>
                <Text style={styles.qrHeading}>{labels.qrBillAmount}</Text>
                <Text style={styles.qrText}>
                  {formatCurrency(totals.invoicedTotal)}
                </Text>
              </View>
            </View>
          </View>

          {/* Right: payment details */}
          <View style={styles.qrPaymentRight}>
            <Text style={styles.qrHeading}>{labels.qrBillPayableTo}</Text>
            <Text style={styles.qrText}>CH31 8080 8008 6983 2167 4</Text>
            <Text style={styles.qrText}>{company.name}</Text>
            <Text style={styles.qrText}>{company.street}</Text>
            <Text style={styles.qrText}>
              {company.postalCode} {company.city}
            </Text>

            {data.qrReference && (
              <>
                <Text style={styles.qrHeading}>{labels.qrBillReference}</Text>
                <Text style={styles.qrText}>
                  {formatQRReference(data.qrReference)}
                </Text>
              </>
            )}

            {data.customerName && (
              <>
                <Text style={styles.qrHeading}>{labels.qrBillPayableBy}</Text>
                <Text style={styles.qrText}>{data.customerName}</Text>
                {data.customerAddress && (
                  <Text style={styles.qrText}>{data.customerAddress}</Text>
                )}
              </>
            )}
          </View>
        </View>
      </View>
    </View>
  );
};

// ─── Main Invoice PDF Document ─────────────────────────────────────

const InvoicePages = ({ data }: { data: InvoicePDFData }) => {
  const totals = calculateTotals(data);
  const nonOptionalGroups = data.content.groups.filter((g) => !g.optional);
  const optionalGroups = data.content.groups.filter((g) => g.optional);
  const labels = getCurrencyLabels(data.language || "de", data.currency);
  const footer = getInvoiceBankDetails(data.currency);

  return (
    <>
      <Page size="A4" style={styles.page}>
        {/* Header with Logo and Title */}
        <View style={styles.headerSection}>
          <View style={styles.leftContainer}>
            <Image style={styles.logo} src={logoImage} />
            <View>
              <Text style={styles.metaLeft}>www.livemotion.ch</Text>
              <Text style={styles.metaLeft}>info@livemotion.ch</Text>
            </View>
          </View>
          <View style={styles.headerRight}>
            <Text style={styles.headerTitle}>{labels.title}</Text>

            <View style={styles.metaSection}>
              <View style={styles.metaRight}>
                <View style={styles.metaRow}>
                  <Text style={styles.metaLabel}>{labels.invoiceNumber}</Text>
                  <Text style={styles.metaValue}>{data.invoiceNumber}</Text>
                </View>
                <View style={styles.metaRow}>
                  <Text style={styles.metaLabel}>{labels.invoiceDate}</Text>
                  <Text style={styles.metaValue}>
                    {formatDate(data.invoiceDate)}
                  </Text>
                </View>
                {data.dueDate && (
                  <View style={styles.metaRow}>
                    <Text style={styles.metaLabel}>{labels.dueDate}</Text>
                    <Text style={styles.metaValue}>
                      {formatDate(data.dueDate)}
                    </Text>
                  </View>
                )}
                <View style={styles.metaRow}>
                  <Text style={styles.metaLabel}>{labels.paymentTerms}</Text>
                  <Text style={styles.metaValue}>
                    {data.paymentTermDays}{" "}
                    {data.language === "fr"
                      ? "jours"
                      : data.language === "en"
                        ? "days"
                        : "Tage"}
                  </Text>
                </View>
              </View>
            </View>

            {/* Customer Address Section */}
            {data.customerName && (
              <View style={styles.customerSection}>
                <Text style={styles.customerLabel}>
                  {labels.customerAddress}
                </Text>
                <Text style={styles.customerName}>{data.customerName}</Text>
                {data.customerAddress && (
                  <Text style={styles.customerAddress}>
                    {data.customerAddress}
                  </Text>
                )}
              </View>
            )}
          </View>
        </View>

        {/* Title Section */}
        <View style={styles.titleSection}>
          <Text style={styles.mainTitle}>
            {labels.title} {data.invoiceTitle}
          </Text>
        </View>

        {/* Project Info Row */}
        <View style={styles.projectInfoRow}>
          <View style={{ width: "50%" }}>
            {data.customerReference && (
              <View style={styles.projectInfoItem}>
                <Text style={styles.projectInfoLabel}>
                  {labels.yourReference}
                </Text>
                <Text style={styles.projectInfoValue}>
                  {data.customerReference}
                </Text>
              </View>
            )}
            {data.contactName && (
              <View style={styles.projectInfoItem}>
                <Text style={styles.projectInfoLabel}>
                  {labels.yourContact}
                </Text>
                <Text style={styles.projectInfoValue}>{data.contactName}</Text>
              </View>
            )}
            {data.accountManagerName && (
              <View style={styles.projectInfoItem}>
                <Text style={styles.projectInfoLabel}>{labels.ourContact}</Text>
                <Text style={styles.projectInfoValue}>
                  {data.accountManagerName}
                </Text>
              </View>
            )}
          </View>
          {(data.eventStartDate || data.eventEndDate) && (
            <View style={styles.projectInfoItem}>
              <Text style={styles.projectInfoLabel}>{labels.eventDate}</Text>
              <Text style={styles.projectInfoValue}>
                {formatShortDateRange(data.eventStartDate, data.eventEndDate)}
              </Text>
            </View>
          )}
        </View>

        {/* Summary Section */}
        <View style={styles.summarySection}>
          <View style={styles.summaryTable}>
            <View style={styles.summaryHeader}>
              <Text
                style={[
                  styles.headerText,
                  styles.summaryColName,
                  styles.summaryTitle,
                ]}
              >
                {labels.overview}
              </Text>
              <Text style={[styles.headerText, styles.summaryColTotal]}>
                {labels.totalCol}
              </Text>
              <Text style={[styles.headerText, styles.summaryColDiscount]}>
                {labels.discount}
              </Text>
              <Text style={[styles.headerText, styles.summaryColNet]}>
                {labels.netPrice}
              </Text>
            </View>
            {nonOptionalGroups.map((group) => {
              const groupGrossTotal = calculateGroupGrossTotal(group.items);
              const groupDiscountTotal = calculateGroupDiscountTotal(
                group.items,
              );
              const groupTotal = calculateGroupTotal(group.items);
              return (
                <View style={styles.summaryRow} key={group.id}>
                  <Text style={[styles.cellText, styles.summaryColName]}>
                    {labels.total} {group.name}
                  </Text>
                  <Text style={[styles.cellText, styles.summaryColTotal]}>
                    {formatCurrency(groupGrossTotal)}
                  </Text>
                  <Text style={[styles.cellText, styles.summaryColDiscount]}>
                    {groupDiscountTotal > 0
                      ? `- ${formatCurrency(groupDiscountTotal)}`
                      : ""}
                  </Text>
                  <Text style={[styles.cellText, styles.summaryColNet]}>
                    {formatCurrency(groupTotal)}
                  </Text>
                </View>
              );
            })}
          </View>
        </View>

        {/* Totals Section */}
        <View style={styles.totalsSection} wrap={false}>
          <View style={styles.totalRowWithBorder}>
            <Text style={styles.totalLabelBold}>{labels.sum}</Text>
            <Text style={styles.totalValueBold}>
              {labels.currencyPrefix} {formatCurrency(totals.netTotal)}
            </Text>
          </View>

          {data.projectDiscountPercent > 0 && (
            <>
              <View style={styles.totalRow}>
                <Text style={styles.totalLabel}>
                  {data.projectDiscountPercent}% {labels.projectDiscount}
                </Text>
                <Text style={styles.totalValue}>
                  - {labels.currencyPrefix}{" "}
                  {formatCurrency(totals.discountAmount)}
                </Text>
              </View>
              <View style={styles.totalRow}>
                <Text style={styles.totalLabel}>{labels.priceExclVat}</Text>
                <Text style={styles.totalValue}>
                  {labels.currencyPrefix}{" "}
                  {formatCurrency(totals.discountedTotal)}
                </Text>
              </View>
            </>
          )}

          {/* Deduction Items */}
          {data.deductions && data.deductions.length > 0 && (
            <>
              {data.deductions.map((deduction, index) => (
                <View key={index} style={styles.totalRow}>
                  <Text style={styles.totalLabel}>{deduction.description}</Text>
                  <Text style={styles.totalValue}>
                    - {labels.currencyPrefix} {formatCurrency(deduction.amount)}
                  </Text>
                </View>
              ))}
              {totals.deductionsTotal > 0 && (
                <View style={styles.totalRow}>
                  <Text style={styles.totalLabel}>{labels.priceExclVat}</Text>
                  <Text style={styles.totalValue}>
                    {labels.currencyPrefix}{" "}
                    {formatCurrency(totals.totalAfterDeductions)}
                  </Text>
                </View>
              )}
            </>
          )}

          {data.vatRate === "CH_8_1" ? (
            <View style={styles.totalRow}>
              <Text style={styles.totalLabel}>
                {labels.vat} ({totals.vatRateValue}%):
              </Text>
              <Text style={styles.totalValue}>
                {labels.currencyPrefix} {formatCurrency(totals.vatAmount)}
              </Text>
            </View>
          ) : (
            <View style={styles.totalRow}>
              <Text style={styles.totalLabel}>{labels.vatReverseCharge}</Text>
              <Text style={styles.totalValue}>
                {labels.currencyPrefix} 0.00
              </Text>
            </View>
          )}

          <View style={styles.totalRowFinal}>
            <Text style={styles.totalLabelBold}>{labels.priceInclVat}</Text>
            <Text style={styles.totalValueFinal}>
              {labels.currencyPrefix} {formatCurrency(totals.grossTotal)}
            </Text>
          </View>

          {totals.depositPercent < 100 && (
            <View
              style={[
                styles.totalRowFinal,
                { marginTop: 4, borderTopWidth: 1, paddingTop: 4 },
              ]}
            >
              <Text style={styles.totalLabelBold}>
                {labels.invoicePercentage} ({totals.depositPercent}%)
              </Text>
              <Text style={styles.totalValueFinal}>
                {labels.currencyPrefix} {formatCurrency(totals.invoicedTotal)}
              </Text>
            </View>
          )}
        </View>

        {/* Options Section */}
        {optionalGroups.length > 0 && (
          <View style={styles.summarySection} wrap={false}>
            <View style={styles.summaryTable}>
              <View style={styles.summaryHeader}>
                <Text
                  style={[
                    styles.headerText,
                    styles.summaryColName,
                    styles.summaryTitle,
                  ]}
                >
                  {labels.options}
                </Text>
                <Text style={[styles.headerText, styles.summaryColTotal]}>
                  {labels.totalCol}
                </Text>
                <Text style={[styles.headerText, styles.summaryColDiscount]}>
                  {labels.discount}
                </Text>
                <Text style={[styles.headerText, styles.summaryColNet]}>
                  {labels.netPrice}
                </Text>
              </View>
              {optionalGroups.map((group) => {
                const groupGrossTotal = calculateGroupGrossTotal(group.items);
                const groupDiscountTotal = calculateGroupDiscountTotal(
                  group.items,
                );
                const groupTotal = calculateGroupTotal(group.items);
                return (
                  <View style={styles.summaryRow} key={group.id}>
                    <Text style={[styles.cellText, styles.summaryColName]}>
                      {group.name}
                    </Text>
                    <Text style={[styles.cellText, styles.summaryColTotal]}>
                      {formatCurrency(groupGrossTotal)}
                    </Text>
                    <Text style={[styles.cellText, styles.summaryColDiscount]}>
                      {groupDiscountTotal > 0
                        ? `- ${formatCurrency(groupDiscountTotal)}`
                        : ""}
                    </Text>
                    <Text style={[styles.cellText, styles.summaryColNet]}>
                      {formatCurrency(groupTotal)}
                    </Text>
                  </View>
                );
              })}
            </View>
          </View>
        )}

        {/* Detail Section (new page) */}
        <View break>
          <Text style={styles.detailTitle}>{labels.detail}</Text>
        </View>

        {/* Non-optional Groups */}
        {nonOptionalGroups.map((group) => (
          <GroupTable key={group.id} group={group} labels={labels} />
        ))}

        {/* Optional Groups */}
        {optionalGroups.map((group) => (
          <GroupTable key={group.id} group={group} isOptional labels={labels} />
        ))}

        {/* Footer */}
        <View style={styles.footer} fixed>
          <View style={styles.footerInner}>
            <View style={styles.footerLeft}>
              <Text style={styles.footerText}>{footer.company}</Text>
              <Text style={styles.footerText}>{footer.bank}</Text>
            </View>
            <Text
              style={styles.footerPageNumber}
              render={({ pageNumber, totalPages }) =>
                `${pageNumber}/${totalPages}`
              }
            />
          </View>
        </View>
      </Page>

      {/* QR-Bill Page (optional) */}
      {data.currency === "CHF" && data.includeQrBill && (
        <Page size="A4" style={styles.qrBillPage}>
          <QRBillSection data={data} totals={totals} labels={labels} />
        </Page>
      )}
    </>
  );
};

const PaymentReminderPage = ({ data }: { data: InvoicePDFData }) => {
  const labels = getCurrencyLabels(data.language || "de", data.currency);
  const footer = getInvoiceBankDetails(data.currency);
  const reminderLabels = getPaymentReminderLabels(data.language || "de");
  const totals = calculateTotals(data);
  const reminderText = data.paymentReminder?.text ?? "";

  return (
    <Page size="A4" style={styles.page}>
      <View style={styles.headerSection}>
        <View style={styles.leftContainer}>
          <Image style={styles.logo} src={logoImage} />
          <View>
            <Text style={styles.metaLeft}>www.livemotion.ch</Text>
            <Text style={styles.metaLeft}>info@livemotion.ch</Text>
          </View>
        </View>
        <View style={styles.headerRight}>
          <Text style={styles.headerTitle}>{reminderLabels.title}</Text>
          <View style={styles.metaSection}>
            <View style={styles.metaRight}>
              <View style={styles.metaRow}>
                <Text style={styles.metaLabel}>{labels.invoiceNumber}</Text>
                <Text style={styles.metaValue}>{data.invoiceNumber}</Text>
              </View>
              <View style={styles.metaRow}>
                <Text style={styles.metaLabel}>
                  {reminderLabels.reminderDate}
                </Text>
                <Text style={styles.metaValue}>
                  {formatDate(data.invoiceDate)}
                </Text>
              </View>
              {data.dueDate && (
                <View style={styles.metaRow}>
                  <Text style={styles.metaLabel}>{labels.dueDate}</Text>
                  <Text style={styles.metaValue}>
                    {formatDate(data.dueDate)}
                  </Text>
                </View>
              )}
              <View style={styles.metaRow}>
                <Text style={styles.metaLabel}>{labels.paymentTerms}</Text>
                <Text style={styles.metaValue}>
                  {data.paymentTermDays} {reminderLabels.days}
                </Text>
              </View>
            </View>
          </View>

          {data.customerName && (
            <View style={styles.customerSection}>
              <Text style={styles.customerLabel}>{labels.customerAddress}</Text>
              <Text style={styles.customerName}>{data.customerName}</Text>
              {data.customerAddress && (
                <Text style={styles.customerAddress}>
                  {data.customerAddress}
                </Text>
              )}
            </View>
          )}
        </View>
      </View>

      <Text style={styles.reminderTitle}>{data.invoiceTitle}</Text>
      <Text style={styles.reminderText}>{reminderText}</Text>

      <View style={styles.reminderAmountBox}>
        <View style={styles.reminderAmountRow}>
          <Text style={styles.reminderAmountLabel}>
            {reminderLabels.amountDue}
          </Text>
          <Text style={styles.reminderAmountValue}>
            {labels.currencyPrefix} {formatCurrency(totals.invoicedTotal)}
          </Text>
        </View>
      </View>

      <View style={styles.footer} fixed>
        <View style={styles.footerInner}>
          <View style={styles.footerLeft}>
            <Text style={styles.footerText}>{footer.company}</Text>
            <Text style={styles.footerText}>{footer.bank}</Text>
          </View>
          <Text
            style={styles.footerPageNumber}
            render={({ pageNumber, totalPages }) =>
              `${pageNumber}/${totalPages}`
            }
          />
        </View>
      </View>
    </Page>
  );
};

export const InvoicePDFDocument = ({ data }: { data: InvoicePDFData }) => {
  if (data.paymentReminder) {
    return (
      <Document>
        <PaymentReminderPage data={data} />
        <InvoicePages data={data.paymentReminder.originalInvoice} />
      </Document>
    );
  }

  return (
    <Document>
      <InvoicePages data={data} />
    </Document>
  );
};

// ─── Public API ────────────────────────────────────────────────────

export async function generateInvoicePDFBlob(
  data: InvoicePDFData,
): Promise<Blob> {
  return await pdf(<InvoicePDFDocument data={data} />).toBlob();
}

/**
 * Prepare invoice data for PDF generation.
 * This transforms the raw invoice data into the format expected by the PDF document,
 * including generating the QR code if needed.
 */
export async function prepareInvoicePDFData(params: {
  invoice: {
    id: string;
    invoice_number: number;
    title: string;
    invoice_date: string | null;
    due_date: string | null;
    payment_term_days: number;
    deposit_percent: number | null;
    project_discount_percent: number;
    vat_rate: "CH_8_1" | "NONE";
    currency: "CHF" | "EUR";
    include_qr_bill: boolean;
    qr_reference: string | null;
  };
  groups: Array<{
    id: string;
    name: string;
    is_optional?: boolean;
    isOptional?: boolean;
    apply_project_discount?: boolean;
    applyProjectDiscount?: boolean;
    items: Array<{
      id: string;
      name: string;
      amount: number;
      factor: number;
      unit_price?: number;
      unitPrice?: number;
      discount: number;
      is_title?: boolean;
      isTitle?: boolean;
      external_note?: string;
      externalNote?: string;
    }>;
  }>;
  deductions: Array<{ description: string; amount: number }>;
  language: string;
  customerName?: string;
  customerAddress?: string;
  contactName?: string;
  accountManagerName?: string;
  eventStartDate?: string;
  eventEndDate?: string;
  customerReference?: string;
}): Promise<InvoicePDFData> {
  const { invoice, groups, deductions, ...rest } = params;

  // Generate QR reference if not already set
  const includeQrBill = invoice.currency === "CHF" && invoice.include_qr_bill;
  const qrReference = includeQrBill
    ? getInvoicePaymentReference(invoice.invoice_number, invoice.qr_reference)
    : null;

  // Generate validated vector QR geometry if the QR-bill is included.
  let qrCodeRects: SwissQRRect[] | undefined;
  if (includeQrBill && qrReference) {
    const grossTotal = calculateGrossTotal(params);
    qrCodeRects = generateSwissQRCode({
      amount: grossTotal,
      reference: qrReference,
      debtorName: rest.customerName,
      debtorAddress: rest.customerAddress,
    });
  }

  // Transform groups into PDF format
  const content: InvoiceContent = {
    groups: groups.map((g) => ({
      id: g.id,
      name: g.name,
      optional: g.isOptional ?? g.is_optional ?? false,
      applyProjectDiscount:
        g.applyProjectDiscount ?? g.apply_project_discount ?? true,
      items: g.items.map((item) => {
        const unitPrice = item.unitPrice ?? item.unit_price ?? 0;
        const isTitle = item.isTitle ?? item.is_title ?? false;
        const total = isTitle
          ? 0
          : item.amount * item.factor * unitPrice * (1 - item.discount / 100);
        return {
          id: item.id,
          name: item.name,
          amount: item.amount,
          factor: item.factor,
          unitPrice,
          discount: item.discount,
          total,
          isTitle,
          externalNote: item.externalNote || item.external_note || undefined,
        };
      }),
    })),
  };

  return {
    invoiceTitle: invoice.title,
    invoiceNumber: String(invoice.invoice_number),
    invoiceDate: invoice.invoice_date || new Date().toISOString().split("T")[0],
    dueDate: invoice.due_date || "",
    paymentTermDays: invoice.payment_term_days,
    depositPercent: invoice.deposit_percent ?? undefined,
    projectDiscountPercent: invoice.project_discount_percent,
    vatRate: invoice.vat_rate,
    currency: invoice.currency,
    content,
    deductions: deductions.length > 0 ? deductions : undefined,
    includeQrBill,
    qrCodeRects,
    qrReference: qrReference || undefined,
    ...rest,
  };
}

// Helper to calculate gross total for QR payload
export function calculateGrossTotal(params: {
  groups: Array<{
    is_optional?: boolean;
    isOptional?: boolean;
    apply_project_discount?: boolean;
    applyProjectDiscount?: boolean;
    items: Array<{
      amount: number;
      factor: number;
      unit_price?: number;
      unitPrice?: number;
      discount: number;
      is_title?: boolean;
      isTitle?: boolean;
    }>;
  }>;
  invoice: {
    project_discount_percent: number;
    vat_rate: "CH_8_1" | "NONE";
    deposit_percent: number | null;
  };
  deductions: Array<{ amount: number }>;
}): number {
  let netTotal = 0;
  let discountEligibleTotal = 0;
  for (const group of params.groups) {
    if (group.isOptional ?? group.is_optional ?? false) continue;
    let groupTotal = 0;
    for (const item of group.items) {
      if (item.isTitle ?? item.is_title ?? false) continue;
      groupTotal +=
        item.amount *
        item.factor *
        (item.unitPrice ?? item.unit_price ?? 0) *
        (1 - item.discount / 100);
    }
    netTotal += groupTotal;
    if (group.applyProjectDiscount ?? group.apply_project_discount ?? true) {
      discountEligibleTotal += groupTotal;
    }
  }

  const discountAmount =
    discountEligibleTotal * (params.invoice.project_discount_percent / 100);
  const afterDiscount = netTotal - discountAmount;
  const deductionsTotal = params.deductions.reduce((s, d) => s + d.amount, 0);
  const afterDeductions = afterDiscount - deductionsTotal;
  const vatRate = params.invoice.vat_rate === "CH_8_1" ? 0.081 : 0;
  const grossTotal = afterDeductions * (1 + vatRate);

  const depositPercent = params.invoice.deposit_percent;
  if (depositPercent && depositPercent > 0 && depositPercent < 100) {
    return Math.round(grossTotal * (depositPercent / 100) * 100) / 100;
  }
  return grossTotal;
}
